import { defineStore } from 'pinia';
import { ref } from 'vue';

import { fetchCrawledImages } from '@/api/order';
import { CrawledImageStatus } from '@/types/types';
import type { CrawledImage, CrawledImageStatusValue } from '@/types/types';

type Entry = {
  status: CrawledImageStatusValue;
  images: CrawledImage[];
};

const DEFAULT_POLL_MS = 5000;

export const useCrawledImagesStore = defineStore('crawledImages', () => {
  const entries = ref<Record<string, Entry>>({});
  const pollers: Record<string, ReturnType<typeof setInterval>> = {};

  function keyFor(orderId: number | string): string {
    return String(orderId);
  }

  async function fetchForOrder(orderId: number | string): Promise<Entry> {
    const key = keyFor(orderId);
    try {
      const response = await fetchCrawledImages(orderId);
      const entry: Entry = {
        status: response?.data?.status ?? CrawledImageStatus.Error,
        images: response?.data?.images ?? [],
      };
      entries.value[key] = entry;
      return entry;
    } catch (error) {
      console.error('Failed to fetch crawled images for order', orderId, error);
      const entry: Entry = { status: CrawledImageStatus.Error, images: [] };
      entries.value[key] = entry;
      return entry;
    }
  }

  function startPolling(orderId: number | string, intervalMs = DEFAULT_POLL_MS) {
    const key = keyFor(orderId);
    if (pollers[key]) return;

    pollers[key] = setInterval(async () => {
      const entry = await fetchForOrder(orderId);
      if (entry.status !== CrawledImageStatus.Pending) {
        stopPolling(orderId);
      }
    }, intervalMs);
  }

  function stopPolling(orderId: number | string) {
    const key = keyFor(orderId);
    if (pollers[key]) {
      clearInterval(pollers[key]);
      delete pollers[key];
    }
  }

  function getEntry(orderId: number | string): Entry | undefined {
    return entries.value[keyFor(orderId)];
  }

  function reset() {
    Object.keys(pollers).forEach((key) => {
      clearInterval(pollers[key]);
      delete pollers[key];
    });
    entries.value = {};
  }

  return {
    entries,
    fetchForOrder,
    startPolling,
    stopPolling,
    getEntry,
    reset,
  };
});
